import { useState } from 'react';
import { DollarSign, CheckCircle2, Clock, XCircle, TrendingUp, Package, ShoppingCart } from 'lucide-react';
import { useCommissions } from '../hooks/useAffiliate';

export default function Commissions() {
  const [statusFilter, setStatusFilter] = useState<'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID' | undefined>(undefined);
  const { data: commissionsResponse, isLoading } = useCommissions({ status: statusFilter });

  // Handle both array and paginated response formats
  const commissions = Array.isArray(commissionsResponse)
    ? commissionsResponse
    : (commissionsResponse?.items || []);

  // Calculate totals
  const pendingTotal = commissions
    .filter(c => c.status === 'PENDING')
    .reduce((sum, c) => sum + c.commissionAmount, 0);

  const approvedTotal = commissions
    .filter(c => c.status === 'APPROVED')
    .reduce((sum, c) => sum + c.commissionAmount, 0);

  const paidTotal = commissions
    .filter(c => c.status === 'PAID')
    .reduce((sum, c) => sum + c.commissionAmount, 0);

  const totalEarned = commissions.reduce((sum, c) => sum + c.commissionAmount, 0);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'PAID':
        return { icon: CheckCircle2, text: 'Paid', textColor: 'text-emerald-600', bgColor: 'bg-emerald-100 dark:bg-emerald-950' };
      case 'APPROVED':
        return { icon: CheckCircle2, text: 'Approved', textColor: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-950' };
      case 'PENDING':
        return { icon: Clock, text: 'Pending', textColor: 'text-amber-600', bgColor: 'bg-amber-100 dark:bg-amber-950' };
      case 'REJECTED':
        return { icon: XCircle, text: 'Rejected', textColor: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-950' };
      default:
        return { icon: Clock, text: status, textColor: 'text-zinc-600', bgColor: 'bg-zinc-100 dark:bg-zinc-800' };
    }
  };

  return (
    <div className="space-y-8 p-6">
      {/* Header */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 pb-6">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Commissions</h1>
        <p className="text-zinc-600 dark:text-zinc-400 text-lg">
          Track your earned commissions and sales performance
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Pending Commissions */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-950 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <span className="text-xs font-bold text-amber-600 bg-amber-100 dark:bg-amber-950 px-2 py-1 rounded">PENDING</span>
          </div>
          <p className="text-2xl font-black text-zinc-900 dark:text-zinc-100">KES {pendingTotal.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 font-medium uppercase tracking-wider mt-1">Pending Review</p>
        </div>

        {/* Approved Commissions */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-950 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-xs font-bold text-blue-600 bg-blue-100 dark:bg-blue-950 px-2 py-1 rounded">APPROVED</span>
          </div>
          <p className="text-2xl font-black text-zinc-900 dark:text-zinc-100">KES {approvedTotal.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 font-medium uppercase tracking-wider mt-1">Ready for Payout</p>
        </div>

        {/* Paid Commissions */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-950 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-xs font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-950 px-2 py-1 rounded">PAID</span>
          </div>
          <p className="text-2xl font-black text-emerald-600">KES {paidTotal.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 font-medium uppercase tracking-wider mt-1">Total Paid Out</p>
        </div>

        {/* Total Earned */}
        <div className="bg-zinc-900 dark:bg-zinc-100 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs font-bold text-white dark:text-zinc-900 bg-white/20 dark:bg-zinc-900/20 px-2 py-1 rounded">TOTAL</span>
          </div>
          <p className="text-2xl font-black text-white dark:text-zinc-900">KES {totalEarned.toLocaleString()}</p>
          <p className="text-xs text-zinc-400 dark:text-zinc-600 font-medium uppercase tracking-wider mt-1">Lifetime Earnings</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-bold text-zinc-700 dark:text-zinc-300">Filter:</span>
        <button
          onClick={() => setStatusFilter(undefined)}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${statusFilter === undefined
            ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900'
            : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700'
            }`}
        >
          All
        </button>
        <button
          onClick={() => setStatusFilter('PENDING')}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${statusFilter === 'PENDING'
            ? 'bg-amber-600 text-white'
            : 'bg-amber-100 dark:bg-amber-950 text-amber-600 hover:bg-amber-200 dark:hover:bg-amber-900'
            }`}
        >
          Pending
        </button>
        <button
          onClick={() => setStatusFilter('APPROVED')}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${statusFilter === 'APPROVED'
            ? 'bg-blue-600 text-white'
            : 'bg-blue-100 dark:bg-blue-950 text-blue-600 hover:bg-blue-200 dark:hover:bg-blue-900'
            }`}
        >
          Approved
        </button>
        <button
          onClick={() => setStatusFilter('PAID')}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${statusFilter === 'PAID'
            ? 'bg-emerald-600 text-white'
            : 'bg-emerald-100 dark:bg-emerald-950 text-emerald-600 hover:bg-emerald-200 dark:hover:bg-emerald-900'
            }`}
        >
          Paid
        </button>
        <button
          onClick={() => setStatusFilter('REJECTED')}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${statusFilter === 'REJECTED'
            ? 'bg-red-600 text-white'
            : 'bg-red-100 dark:bg-red-950 text-red-600 hover:bg-red-200 dark:hover:bg-red-900'
            }`}
        >
          Rejected
        </button>
      </div>

      {/* Commissions List */}
      {isLoading ? (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-12">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-zinc-300 dark:border-zinc-700 border-t-zinc-900 dark:border-t-zinc-100 rounded-full animate-spin mb-4"></div>
            <p className="text-zinc-600 dark:text-zinc-400 font-medium">Loading commissions...</p>
          </div>
        </div>
      ) : commissions.length > 0 ? (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50 dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Sale Amount</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Commission</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {commissions.map((commission) => {
                  const statusConfig = getStatusConfig(commission.status);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <tr key={commission.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-950 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {new Date(commission.createdAt).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-500">
                          {new Date(commission.createdAt).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-zinc-100 dark:bg-zinc-800 rounded flex items-center justify-center">
                            <Package className="w-4 h-4 text-zinc-600 dark:text-zinc-400" />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
                              {commission.link.product.title}
                            </div>
                            <div className="text-xs text-zinc-500 dark:text-zinc-500 font-mono">
                              {commission.link.code}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <ShoppingCart className="w-4 h-4 text-zinc-500" />
                          <div>
                            <div className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
                              #{commission.order.orderNumber}
                            </div>
                            <div className="text-xs text-zinc-500 dark:text-zinc-500">
                              {commission.order.status}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
                          KES {commission.saleAmount.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-base font-black text-emerald-600">
                          KES {commission.commissionAmount.toLocaleString()}
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-500">
                          {commission.commissionType === 'PERCENTAGE'
                            ? `${commission.commissionRate}%`
                            : 'Fixed'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${statusConfig.bgColor}`}>
                          <StatusIcon className={`w-4 h-4 ${statusConfig.textColor}`} />
                          <span className={`text-xs font-bold ${statusConfig.textColor}`}>
                            {statusConfig.text}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-12">
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <DollarSign className="w-10 h-10 text-zinc-400 dark:text-zinc-600" />
            </div>
            <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">No commissions yet</h3>
            <p className="text-zinc-600 dark:text-zinc-400">
              Start sharing your affiliate links to earn commissions
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
